import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const AuthContext = createContext(null);

export function AuthProvider({ value, children }) {
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Simulation-aware role subscription ───────────────────────────────────
// Reads vc_simulated_role from localStorage and listens for the custom
// "vc_simulated_role_change" event (dispatched by RBACContext) so every
// useAuth() consumer re-renders when Owner toggles the "View As" picker.
function useSimulatedRole() {
  const read = () => {
    try { return typeof window !== "undefined" ? (localStorage.getItem("vc_simulated_role") || null) : null; } catch { return null; }
  };
  const [simulated, setSimulated] = useState(read);
  useEffect(() => {
    const handler = () => setSimulated(read());
    if (typeof window !== "undefined") {
      window.addEventListener("vc_simulated_role_change", handler);
      window.addEventListener("storage", handler);
      return () => {
        window.removeEventListener("vc_simulated_role_change", handler);
        window.removeEventListener("storage", handler);
      };
    }
  }, []);
  return simulated;
}

// Same view as useAuth() but null outside an AuthProvider (pages rendered standalone in tests / previews).
export function useOptionalAuth() {
  const ctx = useContext(AuthContext);
  const simulated = useSimulatedRole();
  if (!ctx) return null;
  return simulated ? { ...ctx, role: simulated, cognitoRole: ctx.role } : { ...ctx, cognitoRole: ctx.role };
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  const simulated = useSimulatedRole();
  // cognitoRole ALWAYS reflects the real Cognito role; role reflects the
  // effective view (simulated when set, otherwise real). Downstream code
  // that needs the real role must read cognitoRole explicitly.
  if (simulated) {
    return { ...ctx, role: simulated, cognitoRole: ctx.role };
  }
  return { ...ctx, cognitoRole: ctx.role };
}

// ── Permission matrix ────────────────────────────────────────────────────
// ae          : patients only, no financial tabs visible at all
// technician  : patients + margin locked screen
// intake      : patients + margin locked screen
// nurse       : scoped access — Referrals (view), Scheduler, Patient Notes
// pharmacist  : full clinical access, margin review visible
// owner       : full access including analytics & business financials
export const PERMISSIONS = {
  viewAllPatients: ["owner","pharmacist","ae","intake","technician","liaison","scheduler","nurse"],
  editPatient:     ["owner","pharmacist","intake","technician","liaison","scheduler"],
  deletePatient:   ["owner","pharmacist"],

  // Append notes/updates to patient records (mirrors backend NOTE_ROLES). Nurse
  // gets this for visit documentation — additive grant, no logic change.
  addNotes:        ["owner","pharmacist","technician","intake","ae","liaison","scheduler","billing","nurse"],

  // Full financial + margin access — owner/pharmacist only
  viewFinancials:  ["owner","pharmacist"],

  // Margin tab visibility — ae is HIDDEN, technician/intake see locked screen
  viewMarginTab:   ["owner","pharmacist","technician","intake"],
  // Margin tab full access (no lock screen)
  useMargin:       ["owner","pharmacist"],

  // Analytics — owner only (business financials, monthly/yearly reporting)
  viewAnalytics:   ["owner"],
  // Financial sections inside analytics (drug cost, margin, royalty $)
  viewAnalyticsFinancials: ["owner","pharmacist"],
  viewProjectedPnL:        ["owner"],   // Owner only — projected profit/loss

  // Owner-only financial intelligence (claims, contracts, margin learning)
  viewOwnerFinancials:     ["owner"],

  exportData:   ["owner","pharmacist"],
  manageUsers:  ["owner"],
  viewScheduler: ["owner","pharmacist","scheduler","nurse"],
  // Scheduler write actions (create/reschedule/assign/cancel events). Additive
  // permission — distinct job function; the Scheduler role must be able to book.
  manageSchedule: ["owner","scheduler","pharmacist","technician","nurse"],
};

export function can(role, perm) { return (PERMISSIONS[perm]||[]).includes((role||"").toLowerCase()); }
export function usePermission(perm) { const { role } = useAuth(); return can(role, perm); }

// A session lookup that never settles would leave every API call (and every
// page gated on one) waiting forever, so it is abandoned after this long and the
// request goes out unauthenticated — the backend's 401 then settles the caller.
export const AUTH_TOKEN_TIMEOUT_MS = 15000;
const AUTH_TOKEN_TIMED_OUT = Symbol("auth-token-timeout");

export async function getAuthToken() {
  let timer;
  try {
    const expired = new Promise((resolve) => { timer = setTimeout(() => resolve(AUTH_TOKEN_TIMED_OUT), AUTH_TOKEN_TIMEOUT_MS); });
    const session = await Promise.race([fetchAuthSession({ forceRefresh: false }), expired]);
    if (session === AUTH_TOKEN_TIMED_OUT) {
      console.error(`getAuthToken: session lookup did not finish within ${AUTH_TOKEN_TIMEOUT_MS / 1000} s — continuing unauthenticated`);
      return null;
    }
    return session?.tokens?.idToken?.toString() || null;
  } catch (e) {
    console.error("getAuthToken error:", e);
    return null;
  } finally {
    clearTimeout(timer);
  }
}
